import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PinCodeSignIn from '../../../components/PinCodeSignIn';
import PinCodeSetup from '../../../components/PinCodeSetup';
import BigSpinner from '../../../components/BigSpinner';

import service, { ServiceUnreachableError } from '../../../service';

const SIGN_IN_SHELL =
  'p-6 bg-subtle-grey w-screen h-screen min-h-[600px] flex flex-col justify-center items-center';
const SIGN_IN_CARD =
  'border border-navy-light overflow-hidden flex flex-col w-full h-auto gap-3 sm:w-3/6 lg:w-1/3 xl:w-1/4 bg-white rounded-2xl pb-7';

const SignInMessage = ({ children }) => (
  <div className={SIGN_IN_SHELL}>
    <img className='h-12 mb-10' src='/main-logo-dark.svg' alt='Main Logo' />
    <div className={SIGN_IN_CARD}>
      <div className='pr-6 pl-6 pt-4 pb-4 bg-opacity-25 bg-navy-bright border-b border-navy-light'>
        <p className='text-dark-blue font-bold text-3xl'>Sign In</p>
      </div>
      <div className='px-6 min-h-[80px] flex flex-col justify-center'>{children}</div>
    </div>
  </div>
);

const ForemanSignIn = () => {
  const [searchParams] = useSearchParams();
  // 'loading' | 'needsPin' | 'hasPin' | 'invalidHash' | 'unreachable'
  const [status, setStatus] = useState('loading');
  const [attempt, setAttempt] = useState(0);
  const hash = searchParams.get('h');

  useEffect(() => {
    let cancelled = false;

    const getForemanInfo = async () => {
      if (!hash) {
        setStatus('invalidHash');
        return;
      }
      setStatus('loading');
      try {
        const user = await service.detectForemanStatus(hash);
        if (cancelled) return;
        const foreman = user?.foreman?.[0];
        if (!foreman) {
          setStatus('invalidHash');
          return;
        }
        setStatus(foreman.needsToConfirmPin ? 'needsPin' : 'hasPin');
      } catch (err) {
        if (cancelled) return;
        setStatus(err instanceof ServiceUnreachableError ? 'unreachable' : 'invalidHash');
      }
    };

    getForemanInfo();

    return () => {
      cancelled = true;
    };
  }, [hash, attempt]);

  if (status === 'loading') {
    return (
      <SignInMessage>
        <BigSpinner />
      </SignInMessage>
    );
  }

  if (status === 'unreachable') {
    return (
      <SignInMessage>
        <p className='text-gunite-red font-semibold'>We can't reach the system right now.</p>
        <p className='text-dark-blue mt-2'>
          There was an error on the server. Wait a few minutes and try again.
        </p>
        <button
          className='bg-gunite-red text-white font-semibold px-4 py-2 mt-4 rounded self-start'
          onClick={() => setAttempt(attempt + 1)}
        >
          Try Again
        </button>
      </SignInMessage>
    );
  }

  if (status === 'invalidHash') {
    return (
      <SignInMessage>
        <p className='text-gunite-red font-semibold'>There is a problem with your login code.</p>
        <p className='text-dark-blue mt-2'>
          Verify your QR code/link; if it still does not work, ask your manager for a new code.
        </p>
      </SignInMessage>
    );
  }

  return status === 'needsPin' ? <PinCodeSetup /> : <PinCodeSignIn />;
};

export default ForemanSignIn;
